import React, { useContext } from 'react';
import { createMachine, assign } from 'xstate';
import { useMachine } from '@xstate/react';
import moment from 'moment';
import { dinero, toDecimal } from 'dinero.js';

import { ViewContext } from 'venuescanner-js/react/components/Inbox/context/ViewContext';
import { PaymentCapabilities } from 'venuescanner-js/constants/venue';

import { QuoteSummary, SummaryRow } from './QuoteSummary';
import { PriceForm } from './PriceForm';
import { useGetPaymentCapabilities, useCreateQuotePreview } from './queries';
import { LoadingPanel } from '../../LoadingPanel';

const quoteFormMachine = createMachine({
  id: 'quoteFormMachine',
  initial: 'price',
  states: {
    price: {
      on: {
        NEXT: {
          target: 'summary',
          actions: assign((_context, event) => event.values),
        },
      },
    },
    summary: {
      on: {
        BACK: 'price',
      },
    },
  },
});

const QuoteEditForm = ({
  thread,
  venue,
  onSubmit,
  onCancel,
  onMarkAsBooked,
  onCloseEnquiry,
}) => {
  const {
    activeCurrency: { currency },
  } = useContext(ViewContext);

  const { cumulativeOffer } = thread;

  const { _id: venueId } = venue;

  const [state, send] = useMachine(quoteFormMachine);
  const paymentCapabilitiesQuery = useGetPaymentCapabilities(venueId);
  const quotePreviewMutation = useCreateQuotePreview();

  if (paymentCapabilitiesQuery.isLoading || quotePreviewMutation.isLoading) {
    return <LoadingPanel />;
  }

  const {
    data: { paymentCapabilities },
  } = paymentCapabilitiesQuery.data;

  const defaultValues = {
    offlinePayment:
      (!cumulativeOffer.payment_type &&
        paymentCapabilities === PaymentCapabilities.OFFLINE_PAYMENT) ||
      cumulativeOffer.payment_type === PaymentCapabilities.OFFLINE_PAYMENT,
    vatIncluded:
      cumulativeOffer.vat_included !== undefined
        ? cumulativeOffer.vat_included
        : true,
    vatExempt:
      cumulativeOffer.vat_exempt !== undefined
        ? cumulativeOffer.vat_exempt
        : false,
    price: !isNaN(cumulativeOffer.cost) ? cumulativeOffer.cost : 1,
  };

  if (state.matches('price')) {
    return (
      <PriceForm
        send={send}
        onSubmit={values => {
          quotePreviewMutation.mutate({
            amount: {
              price: values.price * 100,
              currency: currency.code,
            },
            venueId,
            vatIncluded:
              values.vatIncluded || (!values.vatIncluded && values.vatExempt),
            paymentCapabilities,
            offlinePayment: values.offlinePayment,
          });

          send({ type: 'NEXT', values });
        }}
        onCancel={onCancel}
        defaultValues={defaultValues}
        paymentCapabilities={paymentCapabilities}
        currency={currency}
      />
    );
  }

  const {
    data: { quotePreview },
  } = quotePreviewMutation.data;

  // This approach to send the handling fee, commission,
  // and type to the backend is not good at all,
  // all this information should be set by the backend.

  // We pass the handling fee %, commission % and the type of quote
  // to the backend so we can add it in the offer and invoice.
  // This allows us to calculate the prices for online payments
  // based on the information the quote was created with.
  //
  // Unless we rework the flows and move away from  sockets,
  // this will still be done this way.
  const handleSummarySubmit = values => {
    const quote = toQuote(thread, {
      ...state.context,
      ...values,
      handlingFeePercentage: quotePreview.handlingFeePercentage,
      commissionPercentage: quotePreview.commissionPercentage,
      type: quotePreview.type,
    });

    onSubmit(quote);
  };

  return (
    <QuoteSummary
      onGoBack={() => send({ type: 'BACK' })}
      onSubmit={handleSummarySubmit}
      onMarkAsBooked={onMarkAsBooked}
      onCloseEnquiry={onCloseEnquiry}
      onCancel={onCancel}
      submitText={thread.can.REOPEN ? 'Re-open' : 'Send quote'}
      totalPaidAmount={quotePreview.totalPaidAmount}
      hostAmount={quotePreview.hostAmount}
      price={quotePreview.price}
      vat={quotePreview.vat}
      extraCharges={extraCharges(quotePreview)}
      currency={currency}
    />
  );
};

export default QuoteEditForm;

function extraCharges(quotePreview) {
  switch (quotePreview.type) {
    case PaymentCapabilities.ONLINE_COMMISSION_PAYMENT:
      return (
        <SummaryRow
          name='commission'
          value={toDecimal(dinero(quotePreview.commission))}
          label={`Commission (${quotePreview.commissionPercentage}%)`}
        />
      );
    case PaymentCapabilities.ONLINE_SUBSCRIPTION_PAYMENT:
      return (
        <SummaryRow
          name='handling-fee'
          value={toDecimal(dinero(quotePreview.handlingFee))}
          label='Handling fee (2%)'
        />
      );
    default:
      return null;
  }
}

const toQuote = (thread, values) => {
  const { cumulativeOffer } = thread;
  const paymentAndSchedule = values.offlinePayment
    ? willPayOffline(values, thread)
    : willPayFullOnline(values, thread);

  const quote = {
    _id: thread._id,
    message: values.message,
    attachments: values.attachments,
    ...paymentAndSchedule,
    offer: {
      inquire_id: cumulativeOffer.inquire_id,
      cost: values.price,
      amount_in_pence: values.price * 100,
      vat_included: values.offlinePayment ? true : values.vatIncluded,
      vat_exempt: values.offlinePayment ? false : values.vatExempt,
      handling_fee: values.handlingFeePercentage,
      commission: values.commissionPercentage,
      capacity: cumulativeOffer.capacity,
      payment_type: values.type,
      dates: `${moment(new Date(thread.localDates.from)).format(
        'D/M/YYYY',
      )} - ${moment(new Date(thread.localDates.to)).format('D/M/YYYY')}`,
      hours: `${moment(thread.localDates.from).format('HH:mm')} - ${moment(
        thread.localDates.to,
      ).format('HH:mm')}`,
    },
  };

  return quote;
};

function willPayOffline(formValues, thread) {
  return {
    payment: null,
    schedule: [
      {
        paid: 0,
        paidPercent: 0,
        payable: formValues.price,
        percent: 100,
        daysBeforeEvent: null,
        when: new Date(thread.localDates.from),
        onBooking: false,
        payAtEvent: true,
        onlinePayment: false,
      },
    ],
  };
}

function willPayFullOnline(formValues) {
  const { price } = formValues;

  return {
    payment: formValues.price,
    schedule: [
      {
        paid: 0,
        paidPercent: 0,
        payable: price,
        percent: 100,
        daysBeforeEvent: null,
        when: new Date(),
        onBooking: true,
        payAtEvent: false,
        onlinePayment: true,
      },
    ],
  };
}
